// =============================================================================
// PsychometricAuthoringPage — the Vyo-assisted item authoring pipeline.
// Workflow:
//   1. Pick test + sectors + theme + difficulty + count.
//   2. Click "Copy prompt" → templated prompt is on the clipboard.
//   3. Paste into Claude.ai (or any LLM), get back a JSON array.
//   4. Paste the JSON into the textarea here, click "Parse & preview".
//   5. Each item shows in a card with validation status and edit fields.
//   6. Approve / reject / skip per item, edit inline if needed.
//   7. Click "Save approved" — items insert into psychometric_test_items
//      with sector tags pre-populated.
// =============================================================================

import React, { useState, useMemo } from 'react';
import {
  Sparkles, ClipboardCheck, Copy, Check, X, Save, Loader2, AlertTriangle,
  RefreshCw, Eye, EyeOff,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { PageHeader } from '../components/ui/PageHeader';
import { Card } from '../components/ui/Card';
import {
  buildPromptForTest, SUPPORTED_AUTHORING_SLUGS,
  type WatsonGlaserSection,
} from '../lib/psychometric/promptTemplates';
import {
  parseDraftItems, validateItem,
  type DraftItem,
} from '../lib/psychometric/itemValidation';

// ─── Sector picker options ─────────────────────────────────────────────
const SECTOR_OPTIONS: { tag: string; label: string }[] = [
  { tag: 'banking',           label: 'Banking (broad)' },
  { tag: 'banking-ibd',       label: 'Banking · IBD' },
  { tag: 'banking-markets',   label: 'Banking · Markets' },
  { tag: 'banking-quant',     label: 'Banking · Quant' },
  { tag: 'banking-pe',        label: 'Banking · PE' },
  { tag: 'banking-vc',        label: 'Banking · VC' },
  { tag: 'banking-am',        label: 'Banking · Asset Mgmt' },
  { tag: 'banking-retail',    label: 'Banking · Retail' },
  { tag: 'consulting',        label: 'Consulting (broad)' },
  { tag: 'consulting-mbb',    label: 'Consulting · MBB' },
  { tag: 'consulting-strategy', label: 'Consulting · Strategy' },
  { tag: 'big4',              label: 'Big 4 (broad)' },
  { tag: 'big4-audit',        label: 'Big 4 · Audit' },
  { tag: 'big4-tax',          label: 'Big 4 · Tax' },
  { tag: 'big4-advisory',     label: 'Big 4 · Advisory' },
  { tag: 'law',               label: 'Law (broad)' },
  { tag: 'law-magic-circle',  label: 'Law · Magic Circle' },
  { tag: 'tech',              label: 'Tech (broad)' },
  { tag: 'tech-faang',        label: 'Tech · Big tech' },
  { tag: 'civil-service',     label: 'Civil Service / Fast Stream' },
  { tag: 'industry',          label: 'Industry / Corporate' },
  { tag: 'retail',            label: 'Retail / Consumer' },
  { tag: 'fmcg',              label: 'FMCG' },
  { tag: 'energy',            label: 'Energy' },
  { tag: 'media',             label: 'Media' },
  { tag: 'generic',           label: 'Generic (no sector tilt)' },
];

const WATSON_SECTIONS: { value: WatsonGlaserSection; label: string }[] = [
  { value: 'inference',     label: 'Section 1 · Inference (5 options)' },
  { value: 'assumptions',   label: 'Section 2 · Recognition of Assumptions' },
  { value: 'deduction',     label: 'Section 3 · Deduction' },
  { value: 'interpretation', label: 'Section 4 · Interpretation' },
  { value: 'arguments',     label: 'Section 5 · Evaluating Arguments' },
];

interface DraftWithStatus extends DraftItem {
  // Local UI state — not persisted
  _localId: string;
  _decision: 'pending' | 'approved' | 'rejected';
  _validation: { ok: boolean; errors: string[] };
}

export const PsychometricAuthoringPage: React.FC = () => {
  // ── Form state ──
  const [testSlug, setTestSlug] = useState<string>(SUPPORTED_AUTHORING_SLUGS[0].slug);
  const [sectors, setSectors] = useState<string[]>(['generic']);
  const [theme, setTheme] = useState<string>('');
  const [difficulty, setDifficulty] = useState<1 | 2 | 3>(2);
  const [count, setCount] = useState<number>(10);
  const [firmTag, setFirmTag] = useState<string>('');
  const [watsonSection, setWatsonSection] = useState<WatsonGlaserSection>('inference');

  // ── Generation / paste state ──
  const [promptCopied, setPromptCopied] = useState<boolean>(false);
  const [pasteRaw, setPasteRaw] = useState<string>('');
  const [parseError, setParseError] = useState<string | null>(null);

  // ── Draft items state ──
  const [drafts, setDrafts] = useState<DraftWithStatus[]>([]);
  const [showRawJson, setShowRawJson] = useState<Record<string, boolean>>({});

  // ── Save state ──
  const [saving, setSaving] = useState(false);
  const [saveSummary, setSaveSummary] = useState<string | null>(null);

  const promptText = useMemo(() => buildPromptForTest({
    testSlug, testName: '', sectors, theme, difficulty, count,
    firmTag: firmTag.trim() || null,
    watsonSection,
  }), [testSlug, sectors, theme, difficulty, count, firmTag, watsonSection]);

  // ─────────────────────────────────────────────────────────────────────
  function toggleSector(tag: string) {
    setSectors((prev) => prev.includes(tag) ? prev.filter((s) => s !== tag) : [...prev, tag]);
  }

  async function handleCopyPrompt() {
    try {
      await navigator.clipboard.writeText(promptText);
      setPromptCopied(true);
      window.setTimeout(() => setPromptCopied(false), 2000);
    } catch (err) {
      console.warn('clipboard write failed', err);
    }
  }

  function handleParse() {
    setParseError(null);
    try {
      const parsed = parseDraftItems(pasteRaw);
      const withStatus: DraftWithStatus[] = parsed.map((p, i) => ({
        ...p,
        _localId: `${Date.now()}-${i}`,
        _decision: 'pending',
        _validation: validateItem(testSlug, p),
      }));
      setDrafts(withStatus);
      setSaveSummary(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setParseError(msg);
    }
  }

  function approve(localId: string) {
    setDrafts((prev) => prev.map((d) =>
      d._localId === localId ? { ...d, _decision: 'approved' } : d,
    ));
  }
  function reject(localId: string) {
    setDrafts((prev) => prev.map((d) =>
      d._localId === localId ? { ...d, _decision: 'rejected' } : d,
    ));
  }
  function approveAllValid() {
    setDrafts((prev) => prev.map((d) =>
      d._validation.ok ? { ...d, _decision: 'approved' } : d,
    ));
  }
  function clearAll() {
    setDrafts([]);
    setPasteRaw('');
    setSaveSummary(null);
    setParseError(null);
  }
  function toggleRawJson(localId: string) {
    setShowRawJson((prev) => ({ ...prev, [localId]: !prev[localId] }));
  }

  async function handleSave() {
    const approved = drafts.filter((d) => d._decision === 'approved' && d._validation.ok);
    if (approved.length === 0) {
      setSaveSummary('Nothing to save. Approve some items first.');
      return;
    }
    setSaving(true);
    setSaveSummary(null);

    // Look up the test_id for the selected slug.
    const { data: testRow, error: testErr } = await supabase
      .from('psychometric_tests')
      .select('id')
      .eq('slug', testSlug)
      .maybeSingle();
    if (testErr || !testRow) {
      setSaving(false);
      setSaveSummary(`Could not find test slug "${testSlug}" in the DB.`);
      return;
    }
    const testId = (testRow as { id: string }).id;

    // Find the highest existing position so new items append cleanly.
    const { data: posRow } = await supabase
      .from('psychometric_test_items')
      .select('position')
      .eq('test_id', testId)
      .order('position', { ascending: false })
      .limit(1)
      .maybeSingle();
    let nextPos = ((posRow as { position?: number } | null)?.position ?? 0) + 1;

    // Build inserts.
    const rows = approved.map((d) => ({
      test_id: testId,
      position: nextPos++,
      prompt: d.prompt ?? '',
      context: d.context ?? null,
      options: d.options ?? [],
      correct_answer: d.correct_answer ?? null,
      scoring_metadata: d.scoring_metadata ?? {},
    }));

    const { error: insErr, count: insertedCount } = await supabase
      .from('psychometric_test_items')
      .insert(rows, { count: 'exact' });

    setSaving(false);
    if (insErr) {
      setSaveSummary(`Insert failed: ${insErr.message}`);
      return;
    }
    setSaveSummary(`Saved ${insertedCount ?? rows.length} item${rows.length === 1 ? '' : 's'} to ${testSlug}.`);
    // Mark approved items as rejected so they can’t be re-saved on a second click.
    setDrafts((prev) => prev.map((d) =>
      d._decision === 'approved' && d._validation.ok ? { ...d, _decision: 'rejected' } : d,
    ));
  }

  // ── Counts for the summary bar ──
  const total = drafts.length;
  const approvedCount = drafts.filter((d) => d._decision === 'approved' && d._validation.ok).length;
  const rejectedCount = drafts.filter((d) => d._decision === 'rejected').length;
  const invalidCount  = drafts.filter((d) => !d._validation.ok).length;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Psychometric Authoring"
        subtitle="Vyo-assisted item drafting. Pick a test + context, copy the prompt, paste the JSON output, approve items, save."
      />

      {/* ── Step 1 · Form ───────────────────────────────────────────── */}
      <Card>
        <h2 className="text-sm font-semibold text-stone-900 mb-3 inline-flex items-center gap-1.5">
          <Sparkles className="w-4 h-4 text-indigo-500" /> Step 1 · Configure batch
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] font-semibold text-stone-500 mb-1.5">Test</label>
            <select
              value={testSlug}
              onChange={(e) => setTestSlug(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm text-stone-900 focus:outline-none focus:border-indigo-500"
            >
              {SUPPORTED_AUTHORING_SLUGS.map((t) => (
                <option key={t.slug} value={t.slug}>{t.label}</option>
              ))}
            </select>
          </div>

          {testSlug === 'verbal-watson-glaser-v1' && (
            <div>
              <label className="block text-[11px] font-semibold text-stone-500 mb-1.5">Watson-Glaser section</label>
              <select
                value={watsonSection}
                onChange={(e) => setWatsonSection(e.target.value as WatsonGlaserSection)}
                className="w-full px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm text-stone-900 focus:outline-none focus:border-indigo-500"
              >
                {WATSON_SECTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          )}

          <div className="md:col-span-2">
            <label className="block text-[11px] font-semibold text-stone-500 mb-1.5">Sectors (multi-select)</label>
            <div className="flex flex-wrap gap-1.5">
              {SECTOR_OPTIONS.map((opt) => {
                const active = sectors.includes(opt.tag);
                return (
                  <button
                    key={opt.tag}
                    type="button"
                    onClick={() => toggleSector(opt.tag)}
                    className={`inline-flex items-center px-2.5 py-1 rounded-md border text-[11px] font-medium transition-colors ${
                      active
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-stone-200 bg-white text-stone-600 hover:border-indigo-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-stone-500 mb-1.5">Theme / topic (free text)</label>
            <input
              type="text"
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              placeholder="e.g. M&A deal volumes, audit risk, stakeholder management"
              className="w-full px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm text-stone-900 placeholder-stone-400 focus:outline-none focus:border-indigo-500"
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-stone-500 mb-1.5">Marquee firm tag (optional)</label>
            <input
              type="text"
              value={firmTag}
              onChange={(e) => setFirmTag(e.target.value)}
              placeholder="e.g. goldman-sachs, mckinsey, pwc"
              className="w-full px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm text-stone-900 placeholder-stone-400 focus:outline-none focus:border-indigo-500"
            />
            <p className="text-[11px] text-stone-500 mt-1">Adds <code className="text-[10px] bg-stone-100 px-1 rounded">firm-{`<slug>`}</code> to sectors so this item only shows for that firm’s users.</p>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-stone-500 mb-1.5">Difficulty</label>
            <div className="inline-flex rounded-lg border border-stone-200 p-1 bg-white">
              {[1, 2, 3].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDifficulty(d as 1 | 2 | 3)}
                  className={`px-3 py-1.5 rounded-md text-[12px] font-semibold transition-colors ${
                    difficulty === d ? 'bg-indigo-600 text-white' : 'text-stone-600 hover:bg-stone-50'
                  }`}
                >
                  {d === 1 ? 'Easy' : d === 2 ? 'Medium' : 'Hard'}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-stone-500 mb-1.5">Count</label>
            <select
              value={count}
              onChange={(e) => setCount(parseInt(e.target.value, 10))}
              className="w-full px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm text-stone-900 focus:outline-none focus:border-indigo-500"
            >
              {[5, 10, 15, 20, 30, 50].map((n) => (
                <option key={n} value={n}>{n} items</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* ── Step 2 · Copy prompt ────────────────────────────────────── */}
      <Card>
        <h2 className="text-sm font-semibold text-stone-900 mb-3 inline-flex items-center gap-1.5">
          <Copy className="w-4 h-4 text-indigo-500" /> Step 2 · Copy the prompt → paste into Claude.ai
        </h2>
        <div className="flex items-start gap-2">
          <button
            type="button"
            onClick={handleCopyPrompt}
            className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white px-3.5 py-2 rounded-lg text-sm font-semibold"
          >
            {promptCopied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy prompt</>}
          </button>
          <p className="text-[12px] text-stone-500 leading-relaxed flex-1 mt-1">
            Open Claude.ai, paste this prompt, copy the JSON array it returns, then paste below.
          </p>
        </div>
        <details className="mt-3">
          <summary className="cursor-pointer text-[12px] text-stone-500 hover:text-indigo-600">Show prompt preview</summary>
          <pre className="mt-2 max-h-72 overflow-auto bg-stone-50 border border-stone-200 rounded-lg p-3 text-[11px] text-stone-700 whitespace-pre-wrap font-mono">{promptText}</pre>
        </details>
      </Card>

      {/* ── Step 3 · Paste + parse ──────────────────────────────────── */}
      <Card>
        <h2 className="text-sm font-semibold text-stone-900 mb-3 inline-flex items-center gap-1.5">
          <ClipboardCheck className="w-4 h-4 text-indigo-500" /> Step 3 · Paste JSON output
        </h2>
        <textarea
          value={pasteRaw}
          onChange={(e) => setPasteRaw(e.target.value)}
          placeholder="Paste the JSON array from Claude.ai here"
          rows={8}
          className="w-full px-3 py-2 rounded-lg border border-stone-200 bg-white text-[12px] font-mono text-stone-900 placeholder-stone-400 focus:outline-none focus:border-indigo-500"
        />
        <div className="flex items-center gap-2 mt-3">
          <button
            type="button"
            onClick={handleParse}
            className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white px-3.5 py-2 rounded-lg text-sm font-semibold"
          >
            <RefreshCw className="w-4 h-4" /> Parse &amp; preview
          </button>
          {drafts.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="inline-flex items-center gap-1.5 border border-stone-200 hover:bg-stone-50 text-stone-700 px-3 py-2 rounded-lg text-sm font-medium"
            >
              <X className="w-4 h-4" /> Clear
            </button>
          )}
          {parseError && (
            <span className="text-[12px] text-rose-600 inline-flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> {parseError}
            </span>
          )}
        </div>
      </Card>

      {/* ── Step 4 · Review + approve ───────────────────────────────── */}
      {drafts.length > 0 && (
        <Card>
          <div className="flex items-center justify-between gap-4 mb-4">
            <h2 className="text-sm font-semibold text-stone-900 inline-flex items-center gap-1.5">
              <Eye className="w-4 h-4 text-indigo-500" /> Step 4 · Review &amp; approve
            </h2>
            <div className="flex items-center gap-3 text-[12px]">
              <span className="text-stone-500">{total} parsed</span>
              <span className="text-emerald-600 font-medium">{approvedCount} approved</span>
              <span className="text-rose-600 font-medium">{rejectedCount} rejected</span>
              {invalidCount > 0 && <span className="text-amber-600 font-medium">{invalidCount} invalid</span>}
            </div>
          </div>

          <div className="flex items-center gap-2 mb-4">
            <button
              type="button"
              onClick={approveAllValid}
              className="inline-flex items-center gap-1.5 border border-emerald-200 hover:bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-md text-[12px] font-medium"
            >
              <Check className="w-3.5 h-3.5" /> Approve all valid
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={approvedCount === 0 || saving}
              className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-3.5 py-2 rounded-lg text-sm font-semibold ml-auto"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Saving' : `Save ${approvedCount} approved`}
            </button>
          </div>

          {saveSummary && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-800 mb-4">
              {saveSummary}
            </div>
          )}

          <div className="space-y-3">
            {drafts.map((d, idx) => (
              <DraftCard
                key={d._localId}
                index={idx + 1}
                draft={d}
                rawShown={!!showRawJson[d._localId]}
                onApprove={() => approve(d._localId)}
                onReject={() => reject(d._localId)}
                onToggleRaw={() => toggleRawJson(d._localId)}
              />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

// ─── DraftCard ───────────────────────────────────────────────────────────
interface DraftCardProps {
  index: number;
  draft: DraftWithStatus;
  rawShown: boolean;
  onApprove: () => void;
  onReject: () => void;
  onToggleRaw: () => void;
}

const DraftCard: React.FC<DraftCardProps> = ({ index, draft, rawShown, onApprove, onReject, onToggleRaw }) => {
  const status = draft._decision;
  const valid = draft._validation.ok;
  const borderClass =
    status === 'approved' ? 'border-emerald-300 bg-emerald-50/30' :
    status === 'rejected' ? 'border-rose-200 bg-rose-50/30' :
    !valid                ? 'border-amber-300 bg-amber-50/30' :
                            'border-stone-200 bg-white';

  return (
    <div className={`rounded-lg border ${borderClass} p-4 transition-colors`}>
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="text-[11px] font-semibold text-stone-500">
          Item {index}
          {!valid && <span className="ml-2 text-amber-600">⚠ {draft._validation.errors.length} validation issue{draft._validation.errors.length === 1 ? '' : 's'}</span>}
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={onToggleRaw} title="Toggle raw JSON" className="p-1 rounded hover:bg-stone-100 text-stone-500">
            {rawShown ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
          {status !== 'approved' && (
            <button
              onClick={onApprove}
              disabled={!valid}
              className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white px-2.5 py-1 rounded text-[11px] font-semibold"
            >
              <Check className="w-3 h-3" /> Approve
            </button>
          )}
          {status !== 'rejected' && (
            <button
              onClick={onReject}
              className="inline-flex items-center gap-1 bg-rose-600 hover:bg-rose-700 text-white px-2.5 py-1 rounded text-[11px] font-semibold"
            >
              <X className="w-3 h-3" /> Reject
            </button>
          )}
        </div>
      </div>

      {!valid && (
        <ul className="text-[11px] text-amber-700 mb-2 space-y-0.5">
          {draft._validation.errors.map((e, i) => <li key={i}>· {e}</li>)}
        </ul>
      )}

      {draft.context && (
        <p className="text-[12px] text-stone-600 italic mb-2 leading-relaxed">{draft.context}</p>
      )}
      <p className="text-[13px] text-stone-900 font-medium mb-2">{draft.prompt}</p>

      {Array.isArray(draft.options) && (
        <ul className="space-y-1 mb-2">
          {draft.options.map((opt) => (
            <li key={opt.id} className={`text-[12px] flex items-start gap-2 ${
              isCorrectOption(opt.id, draft.correct_answer ?? '') ? 'text-emerald-700 font-semibold' : 'text-stone-700'
            }`}>
              <span className="font-mono text-[10px] text-stone-400 shrink-0 pt-0.5">{opt.id}</span>
              <span>{opt.text || <em className="text-stone-400">(visual option, no text)</em>}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2 mt-2 text-[10px]">
        {Array.isArray(draft.scoring_metadata?.sectors) && (draft.scoring_metadata!.sectors as string[]).map((s) => (
          <span key={s} className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-medium">{s}</span>
        ))}
        {draft.scoring_metadata?.difficulty != null && (
          <span className="px-1.5 py-0.5 rounded bg-stone-100 text-stone-600 font-medium">difficulty {String(draft.scoring_metadata.difficulty)}</span>
        )}
        {typeof draft.scoring_metadata?.section === 'string' && (
          <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">section: {draft.scoring_metadata.section}</span>
        )}
        {typeof draft.scoring_metadata?.stage === 'number' && (
          <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">stage {draft.scoring_metadata.stage}</span>
        )}
      </div>

      {rawShown && (
        <pre className="mt-3 max-h-72 overflow-auto bg-stone-50 border border-stone-200 rounded p-2 text-[10px] text-stone-700 font-mono">
          {JSON.stringify(draft, null, 2)}
        </pre>
      )}
    </div>
  );
};

function isCorrectOption(optionId: string, correctAnswer: string): boolean {
  if (!correctAnswer) return false;
  if (optionId === correctAnswer) return true;
  // SJT correct_answer is JSON like {"most":"b","least":"d"} — check both.
  try {
    const parsed = JSON.parse(correctAnswer);
    return parsed?.most === optionId || parsed?.least === optionId;
  } catch {
    return false;
  }
}
